import { CardPayment } from '@mercadopago/sdk-react';
import { memo, useMemo } from 'react';
import { soles } from '../../../compartido/formato';

type Envio = Parameters<Parameters<typeof CardPayment>[0]['onSubmit']>;

/**
 * El formulario de tarjeta de Mercado Pago, aislado del resto de la página.
 *
 * El SDK destruye y vuelve a crear el formulario cada vez que cambia la
 * identidad de cualquiera de sus props. Por eso: memo, opciones calculadas
 * una vez con useMemo, y callbacks estables (ver useEstable). Si la página
 * de pago se redibuja (la cuenta regresiva, la consulta de estado), esto no
 * se entera.
 */
export const FormularioTarjeta = memo(function FormularioTarjeta({
  precio,
  correo,
  onPagar,
  onListo,
  onFallo,
}: {
  /** En céntimos. */
  precio: number;
  correo: string | undefined;
  onPagar: (...args: Envio) => Promise<void>;
  onListo: () => void;
  onFallo: (critico: boolean) => void;
}) {
  const inicializacion = useMemo(() => ({ amount: precio / 100, ...(correo ? { payer: { email: correo } } : {}) }), [precio, correo]);

  const personalizacion = useMemo(
    () => ({
      paymentMethods: { types: { included: ['credit_card', 'debit_card', 'prepaid_card'] as ('credit_card' | 'debit_card' | 'prepaid_card')[] } },
      visual: {
        style: {
          theme: 'default',
          customVariables: {
            baseColor: '#85682E',
            baseColorFirstVariant: '#6B5324',
            baseColorSecondVariant: '#FAF0DC',
            textPrimaryColor: '#1F1A16',
            textSecondaryColor: '#6B5F52',
            inputBackgroundColor: '#FFFFFF',
            formBackgroundColor: '#FFFFFF',
            outlinePrimaryColor: '#C4B8A8',
            errorColor: '#9C4A34',
            borderRadiusMedium: '10px',
            borderRadiusLarge: '12px',
          },
        },
        texts: { formSubmit: `Pagar ${soles(precio)}` },
      },
    }),
    [precio],
  );

  const alFallar = useMemo(() => (e: { type: string }) => onFallo(e.type === 'critical'), [onFallo]);

  return (
    <CardPayment
      locale="es-PE"
      initialization={inicializacion}
      customization={personalizacion}
      onSubmit={onPagar}
      onReady={onListo}
      onError={alFallar}
    />
  );
});
